import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@customerio/pluma-components/react';
import { getObjectTypesQueryOptions } from '@/services/db/object-type/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { toIdList } from './multi-select-values';

type ObjectTypeFilterProps = {
  selectedObjectTypeIds: readonly string[];
  onObjectTypeIdsChange: (objectTypeIds: string[]) => void;
};

/**
 * The workspace's object types. Not the suspense read the entity exports: a filter bar waiting
 * on its catalog would take the whole list down to a loader with it.
 */
function useObjectTypeCatalog() {
  return useQuery(getObjectTypesQueryOptions(requireWorkspaceId(), useQueryClient()));
}

/** Whether the workspace has any object types, which is when an object type filter is offered. */
export function useHasObjectTypes(): boolean {
  return (useObjectTypeCatalog().data?.length ?? 0) > 0;
}

/** Filters a list by the object types its items relate to. */
export function ObjectTypeFilter({ selectedObjectTypeIds, onObjectTypeIdsChange }: ObjectTypeFilterProps) {
  const options = (useObjectTypeCatalog().data ?? []).map((objectType) => ({
    value: objectType.id,
    label: objectType.name,
  }));

  return (
    <Select
      size="sm"
      isMulti
      isSearchable
      options={options}
      placeholder="Search object types"
      ariaLabel="Object types"
      value={[...selectedObjectTypeIds]}
      onChange={(value: string | string[] | null) => onObjectTypeIdsChange(toIdList(value))}
    />
  );
}
